import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import axios from "axios";
import { ArrowLeft, Minus, Plus, ShoppingCart, Check, Star, Heart, Palette, ZoomIn, Pencil, Save, X, Upload, Loader2, Copy, Search, Share2, Mail, Trash2, ImageIcon, Ruler, Package } from "lucide-react";
import { Editor } from '@tinymce/tinymce-react';
import { Button } from "@/components/ui/button";
import BackButton from "@/components/BackButton";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import HexisColorPalette from "@/components/HexisColorPalette";
import { ImageLightbox } from "@/components/ImageLightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { addToCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { formatPrice } = useCurrency();
  const { translateText, language, t } = useLanguage();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedColor2, setSelectedColor2] = useState(null);
  const [activeColorSlot, setActiveColorSlot] = useState(1); // 1 or 2 for dual color products
  const [selectedHexisColor, setSelectedHexisColor] = useState(null);
  const [selectedHexisColor2, setSelectedHexisColor2] = useState(null);
  const [pendingHexisColor, setPendingHexisColor] = useState(null);
  const [showHexisPalette, setShowHexisPalette] = useState(false);
  const [orderInstructions, setOrderInstructions] = useState("");
  const [imageError, setImageError] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  
  // Vinyl Tools state
  const [vinylTools, setVinylTools] = useState([]);
  const [selectedTools, setSelectedTools] = useState([]);
  const [showToolsDropdown, setShowToolsDropdown] = useState(false);

  // Check if product is a Wrap Vinyl product (should NOT show HEXIS palette)
  const isWrapVinylProduct = product && (
    product.category_slug?.toLowerCase().includes('wrap-vinyl') ||
    product.category_slug?.toLowerCase().includes('hx20000') ||
    product.category_slug?.toLowerCase().includes('hx30000') ||
    product.category_slug?.toLowerCase().includes('wrap-tools') ||
    product.category_name?.toLowerCase().includes('wrap vinyl') ||
    product.category_name?.toLowerCase().includes('hexis')
  );
  const toolsDropdownRef = useRef(null);
  
  // Translated content state
  const [translatedName, setTranslatedName] = useState("");
  const [translatedDescription, setTranslatedDescription] = useState("");
  const [showShareMenu, setShowShareMenu] = useState(false);
  
  // Customer material selection state
  const [customerSelectedMaterial, setCustomerSelectedMaterial] = useState(null);
  
  // Admin Edit mode states
  const [isEditing, setIsEditing] = useState(searchParams.get('edit') === 'true');
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    price: 0,
    meta_title: "",
    meta_description: "",
    seo_slug: "",
    image_url: "",
    has_color_palette: true,
    requires_two_colors: false,
    color1_label: "Colour 1",
    color2_label: "Colour 2",
    category_id: "",
    category_name: "",
    subcategory: "",
    material_id: "",
    show_material: false,
    related_products: []
  });
  const [saving, setSaving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [editingMaterialPrices, setEditingMaterialPrices] = useState(false);
  const [materialPriceEdits, setMaterialPriceEdits] = useState({});
  const [savingMaterials, setSavingMaterials] = useState(false);
  
  // Related products state
  const [allProducts, setAllProducts] = useState([]);
  const [relatedSearchTerm, setRelatedSearchTerm] = useState("");
  const [showRelatedPicker, setShowRelatedPicker] = useState(false);
  const [loadingAllProducts, setLoadingAllProducts] = useState(false);
  
  // Rich text editor configuration
  // Image picker state for TinyMCE
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [fileManagerImages, setFileManagerImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [imagePickerCallback, setImagePickerCallback] = useState(null);
  const [imageSearchTerm, setImageSearchTerm] = useState("");
  const [imagePickerTab, setImagePickerTab] = useState("filemanager"); // filemanager, upload, external
  const [uploadingPickerImage, setUploadingPickerImage] = useState(false);
  const [externalImageUrl, setExternalImageUrl] = useState("");
  const [externalImageAlt, setExternalImageAlt] = useState("");

  // Fetch images for the picker
  const fetchFileManagerImages = async () => {
    setLoadingImages(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.get(`${API}/admin/website-images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFileManagerImages(res.data.images || []);
    } catch (error) {
      console.error("Failed to fetch images:", error);
    } finally {
      setLoadingImages(false);
    }
  };

  // Fetch all products for related products picker
  const fetchAllProducts = async () => {
    setLoadingAllProducts(true);
    try {
      const res = await axios.get(`${API}/products?limit=500`);
      // API returns array directly, filter out the current product
      const products = Array.isArray(res.data) ? res.data : (res.data.products || []);
      const filtered = products.filter(p => p.id !== id);
      setAllProducts(filtered);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setLoadingAllProducts(false);
    }
  };

  // TinyMCE editor configuration with File Manager integration
  const tinymceInit = useMemo(() => ({
    height: 400,
    menubar: 'file edit insert view format table tools',
    plugins: [
      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
      'insertdatetime', 'media', 'table', 'help', 'wordcount'
    ],
    toolbar: 'undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | link image | socialicons | code',
    // Allow SVG elements for social icons
    extended_valid_elements: 'svg[xmlns|viewBox|fill|class|width|height|style],path[d|fill|class],g[*],circle[*],rect[*]',
    valid_children: '+body[svg],+p[svg],+span[svg]',
    content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; font-size: 14px } .social-icon { display: inline-block; width: 24px; height: 24px; vertical-align: middle; margin: 0 4px; }',
    branding: false,
    promotion: false,
    license_key: 'gpl',
    file_picker_types: 'image',
    file_picker_callback: (callback, value, meta) => {
      if (meta.filetype === 'image') {
        setImagePickerCallback(() => callback);
        fetchFileManagerImages();
        setShowImagePicker(true);
      }
    },
    image_title: true,
    image_description: true,
    image_dimensions: true,
    image_advtab: true,
    setup: (editor) => {
      // Social Media Icons - Single Color SVGs
      const socialIcons = [
        { name: 'Facebook', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>' },
        { name: 'Instagram', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>' },
        { name: 'Twitter/X', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>' },
        { name: 'YouTube', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>' },
        { name: 'TikTok', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>' },
        { name: 'LinkedIn', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>' },
        { name: 'Pinterest', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/></svg>' },
        { name: 'WhatsApp', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>' },
        { name: 'Snapchat', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03c-.135 0-.313-.031-.538-.074-.36-.075-.765-.135-1.273-.135-.3 0-.599.015-.913.074-.6.104-1.123.464-1.723.884-.853.599-1.826 1.288-3.294 1.288-.06 0-.119-.015-.18-.015h-.149c-1.468 0-2.427-.675-3.279-1.288-.599-.42-1.107-.779-1.707-.884-.314-.045-.629-.074-.928-.074-.54 0-.958.089-1.272.149-.211.043-.391.074-.54.074-.374 0-.523-.224-.583-.42-.061-.192-.09-.389-.135-.567-.046-.181-.105-.494-.166-.57-1.918-.222-2.95-.642-3.189-1.226-.031-.063-.052-.15-.055-.225-.015-.243.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.434-.884-.658-1.332-.809-.121-.029-.24-.074-.346-.119-1.107-.435-1.257-.93-1.197-1.273.09-.479.674-.793 1.168-.793.146 0 .27.029.383.074.42.194.789.3 1.104.3.234 0 .384-.06.465-.105l-.046-.569c-.098-1.626-.225-3.651.307-4.837C7.392 1.077 10.739.807 11.727.807l.419-.015h.06z"/></svg>' },
        { name: 'Telegram', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>' },
        { name: 'Email', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>' },
        { name: 'Website', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>' },
        { name: 'Phone', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>' },
      ];
      
      editor.ui.registry.addMenuButton('socialicons', {
        text: 'Social Icons',
        tooltip: 'Insert Social Media Icon',
        fetch: (callback) => {
          const items = socialIcons.map(social => ({
            type: 'menuitem',
            text: social.name,
            onAction: () => {
              // Insert icon with a unique marker, then position cursor after it
              const uniqueId = 'social-icon-' + Date.now();
              editor.insertContent(`<span id="${uniqueId}" class="social-icon-wrapper" style="display:inline;">${social.icon}</span>&nbsp;`);
              
              // Find the inserted element and move cursor after it
              const insertedNode = editor.dom.get(uniqueId);
              if (insertedNode) {
                // Remove the temporary ID
                insertedNode.removeAttribute('id');
                // Move cursor after the inserted node
                editor.selection.select(insertedNode);
                editor.selection.collapse(false);
              }
            }
          }));
          callback(items);
        }
      });
    }
  }), []);
  
  // A5 Sample mode
  const [sampleMode, setSampleMode] = useState(false);
  const [sampleColor, setSampleColor] = useState(null);
  const [addingSample, setAddingSample] = useState(false);
  
  // Review states
  const [reviews, setReviews] = useState([]);
  const [reviewTitle, setReviewTitle] = useState("");
  const [reviewText, setReviewText] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submittingReview, setSubmittingReview] = useState(false);
  
  // Check if current user is admin
  const isAdmin = user?.role === 'admin' || user?.is_admin;
  
  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  
  // Close Application Tools dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (toolsDropdownRef.current && !toolsDropdownRef.current.contains(event.target)) {
        setShowToolsDropdown(false);
      }
    };
    
    if (showToolsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showToolsDropdown]);
  
  // Fetch categories and materials
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, matRes] = await Promise.all([
          axios.get(`${API}/categories/all`),
          axios.get(`${API}/materials`)
        ]);
        setCategories(catRes.data);
        setMaterials(matRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);
  
  // Initialize edit form when product loads
  useEffect(() => {
    if (product) {
      setEditForm({
        name: product.name || "",
        description: product.description || "",
        price: product.price || 0,
        meta_title: product.meta_title || "",
        meta_description: product.meta_description || "",
        seo_slug: product.seo_slug || "",
        image_url: product.image_url || "",
        has_color_palette: product.has_color_palette !== false,
        requires_two_colors: product.requires_two_colors || false,
        color1_label: product.color1_label || "Colour 1",
        color2_label: product.color2_label || "Colour 2",
        category_id: product.category_id || "",
        category_name: product.category_name || "",
        subcategory: product.subcategory || "",
        material_id: product.material_id || "",
        show_material: product.show_material !== false && !!product.material_id,
        related_products: product.related_products || []
      });
      setImagePreview(null);
    }
  }, [product]);
  
  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload JPEG, PNG, WebP, or GIF");
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 5MB");
      return;
    }
    
    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
    
    // Upload file
    setUploadingImage(true);
    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/products/${id}/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        setEditForm({ ...editForm, image_url: response.data.image_url });
        setProduct({ ...product, image_url: response.data.image_url });
        toast.success("Image uploaded successfully!");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error(error.response?.data?.detail || "Failed to upload image");
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };
  
  // Save product changes
  const handleSaveProduct = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(`${API}/products/${id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local product state
      setProduct({ ...product, ...editForm });
      setIsEditing(false);
      toast.success("Product updated successfully!");
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  // Duplicate product listing
  const handleDuplicateProduct = async () => {
    setDuplicating(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      // Create a copy of the product data without the id
      const duplicateData = {
        name: `${product.name} (Copy)`,
        description: product.description || "",
        price: product.price || 0,
        image_url: product.image_url || "",
        images: product.images || [],
        category_id: product.category_id || "",
        category_name: product.category_name || "",
        subcategory: product.subcategory || "",
        stock: product.stock || 0,
        color_options: product.color_options || [],
        has_color_palette: product.has_color_palette !== false,
        material_id: product.material_id || "",
        show_material: product.show_material || false,
        meta_title: product.meta_title ? `${product.meta_title} (Copy)` : "",
        meta_description: product.meta_description || "",
        seo_slug: product.seo_slug ? `${product.seo_slug}-copy` : "",
      };
      
      // Use the admin endpoint for creating products
      const response = await axios.post(`${API}/admin/products`, duplicateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.id) {
        toast.success("Product duplicated successfully!", {
          description: "Opening edit mode..."
        });
        // Navigate to the new product's edit page with a small delay
        setTimeout(() => {
          window.location.href = `/product/${response.data.id}?edit=true`;
        }, 500);
      }
    } catch (error) {
      console.error("Error duplicating product:", error);
      toast.error(error.response?.data?.detail || "Failed to duplicate product");
    } finally {
      setDuplicating(false);
    }
  };

  // Delete product listing
  const handleDeleteProduct = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      await axios.delete(`${API}/admin/products/${product.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Product deleted successfully!", {
        description: "Redirecting to shop..."
      });
      
      // Navigate back to shop
      setTimeout(() => {
        window.location.href = '/shop';
      }, 500);
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error(error.response?.data?.detail || "Failed to delete product");
      setDeleting(false);
    }
  };

  // Save material price edits
  const saveMaterialPrices = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    
    setSavingMaterials(true);
    try {
      const updates = Object.entries(materialPriceEdits);
      for (const [materialId, newPrice] of updates) {
        await axios.patch(`${API}/materials/${materialId}`, 
          { price_modifier: parseFloat(newPrice) || 0 },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      // Refresh materials list
      const res = await axios.get(`${API}/materials`);
      setMaterials(res.data);
      setEditingMaterialPrices(false);
      setMaterialPriceEdits({});
      toast.success("Material prices updated!");
    } catch (error) {
      console.error("Error saving material prices:", error);
      toast.error("Failed to save material prices");
    } finally {
      setSavingMaterials(false);
    }
  };

  // Generate structured data for SEO
  const generateStructuredData = () => {
    if (!product) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.name,
      "description": product.meta_description || product.description?.slice(0, 200),
      "image": product.image_url,
      "sku": product.sku,
      "brand": {
        "@type": "Brand",
        "name": "Mini Stripes UK"
      },
      "offers": {
        "@type": "Offer",
        "url": window.location.href,
        "priceCurrency": "GBP",
        "price": product.price,
        "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "seller": {
          "@type": "Organization",
          "name": "Mini Stripes UK"
        }
      },
      "category": product.category_name
    };
  };

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/products/${id}`);
        setProduct(res.data);
        
        // Set default color if available
        if (res.data.color_options && res.data.color_options.length > 0) {
          setSelectedColor(res.data.color_options[0]);
        }
        
        // Fetch reviews
        try {
          const reviewsRes = await axios.get(`${API}/products/${id}/reviews`);
          setReviews(reviewsRes.data || []);
        } catch (err) {
          setReviews([]);
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
    window.scrollTo(0, 0);
  }, [id]);

  // Fetch vinyl tools for Mini products
  useEffect(() => {
    const fetchVinylTools = async () => {
      if (!product) return;
      
      // Check if it's a Mini product (by name or category)
      const isMiniProduct = product.name?.toLowerCase().includes('mini') || 
                           product.category_slug?.toLowerCase().includes('mini') ||
                           product.category_name?.toLowerCase().includes('mini');
      
      if (isMiniProduct) {
        try {
          const res = await axios.get(`${API}/products?category=vinyl-tools&limit=10`);
          setVinylTools(res.data || []);
        } catch (error) {
          console.error("Error fetching vinyl tools:", error);
        }
      }
    };
    fetchVinylTools();
  }, [product]);

  // Translate product name and description when language changes
  useEffect(() => {
    let isMounted = true;
    
    const translateContent = async () => {
      if (!product) return;
      
      // Set original values first
      setTranslatedName(product.name || "");
      setTranslatedDescription(product.description || "");
      
      // If English, no translation needed
      if (language === 'en') return;
      
      try {
        // Translate name
        const name = await translateText(product.name);
        if (isMounted && name) {
          setTranslatedName(name);
        }
        
        // Translate description (strip HTML for translation, then re-apply)
        if (product.description) {
          // Extract text content from HTML
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = product.description;
          const textContent = tempDiv.textContent || tempDiv.innerText || '';
          
          if (textContent.trim()) {
            const translatedDesc = await translateText(textContent);
            if (isMounted && translatedDesc) {
              // Wrap in paragraph tag for consistent rendering
              setTranslatedDescription(`<p>${translatedDesc}</p>`);
            }
          }
        }
      } catch (error) {
        console.error('Translation error:', error);
      }
    };
    
    translateContent();
    
    return () => {
      isMounted = false;
    };
  }, [product, language, translateText]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewTitle.trim() || !reviewText.trim() || rating === 0) {
      toast.error("Please fill in all fields and select a rating");
      return;
    }
    
    setSubmittingReview(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/products/${id}/reviews`, {
        title: reviewTitle,
        text: reviewText,
        rating: rating
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      toast.success("Review submitted successfully!");
      setReviewTitle("");
      setReviewText("");
      setRating(0);
      
      // Refresh reviews
      const reviewsRes = await axios.get(`${API}/products/${id}/reviews`);
      setReviews(reviewsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleAddToCart = async () => {
    setAdding(true);
    
    // Determine the color to use - HEXIS color takes priority if selected
    let colorToUse = selectedColor;
    let colorDetails = "";
    
    if (selectedHexisColor) {
      colorToUse = `HEXIS ${selectedHexisColor.code}`;
      colorDetails = `${selectedHexisColor.name} (${selectedHexisColor.code})`;
      if (selectedHexisColor.series) colorDetails += ` - ${selectedHexisColor.series}`;
      if (selectedHexisColor.finish) colorDetails += ` ${selectedHexisColor.finish}`;
    }
    
    // Handle dual color products
    let color2Details = "";
    if (product.requires_two_colors && selectedHexisColor2) {
      color2Details = `${selectedHexisColor2.name} (${selectedHexisColor2.code})`;
      if (selectedHexisColor2.series) color2Details += ` - ${selectedHexisColor2.series}`;
      if (selectedHexisColor2.finish) color2Details += ` ${selectedHexisColor2.finish}`;
    }
    
    // Append HEXIS color details to instructions if selected
    let finalInstructions = orderInstructions;
    if (selectedHexisColor && colorDetails) {
      const color1Label = product.color1_label || 'Colour 1';
      finalInstructions = `${product.requires_two_colors ? color1Label : 'Colour'}: ${colorDetails}`;
      if (product.requires_two_colors && color2Details) {
        const color2Label = product.color2_label || 'Colour 2';
        finalInstructions += `\n${color2Label}: ${color2Details}`;
      }
      if (orderInstructions) {
        finalInstructions += '\n\n' + orderInstructions;
      }
    }
    
    // Calculate price override for premium vinyl series
    const isSkintacColor = selectedHexisColor?.series === "HX20000";
    const isSuptacHXColor = selectedHexisColor?.series === "HXS5000";
    let priceOverride = null;
    let displayPrice = product.price;
    
    if (isSkintacColor) {
      priceOverride = product.price * 1.5;
      displayPrice = priceOverride;
    } else if (isSuptacHXColor) {
      priceOverride = product.price * 1.4;
      displayPrice = priceOverride;
    }
    
    const result = await addToCart(product.id, quantity, colorToUse, finalInstructions, priceOverride, product.category_slug || product.category);
    if (result.success) {
      let colorDisplay = selectedHexisColor ? selectedHexisColor.name : selectedColor;
      if (product.requires_two_colors && selectedHexisColor2) {
        colorDisplay += ` + ${selectedHexisColor2.name}`;
      }
      toast.success("Added to cart", {
        description: `${quantity}x ${translatedName || product.name}${colorDisplay ? ` (${colorDisplay})` : ''} - £${displayPrice.toFixed(2)}`
      });
      setOrderInstructions(""); // Clear instructions after adding
      
      // Also add selected vinyl tools to cart
      if (selectedTools.length > 0) {
        for (const tool of selectedTools) {
          await addToCart(tool.id, 1, null, null, null, 'vinyl-tools');
        }
        toast.success(`${selectedTools.length} application tool${selectedTools.length > 1 ? 's' : ''} added to cart`);
        setSelectedTools([]); // Clear selected tools
      }
    } else {
      toast.error("Failed to add to cart");
    }
    setAdding(false);
  };

  // Handle adding A5 sample to cart
  const handleAddSampleToCart = async () => {
    if (!sampleColor) {
      toast.error("Please select a colour for the sample");
      return;
    }
    
    setAddingSample(true);
    
    const instructions = `A5 COLOUR SAMPLE SWATCH
Colour: ${sampleColor.name} (${sampleColor.code})
Series: ${sampleColor.series || 'HEXIS'}
Finish: ${sampleColor.finish || 'Standard'}
Size: A5 (148 x 210mm)`;

    const result = await addToCart(
      "a5-colour-sample",
      1,
      `HEXIS ${sampleColor.code}`,
      instructions,
      4.99 // Fixed price for A5 sample
    );

    if (result.success) {
      toast.success("A5 Sample added to cart!", {
        description: `${sampleColor.name} - £4.99`
      });
      setSampleMode(false);
      setSampleColor(null);
    } else {
      toast.error("Failed to add sample to cart");
    }
    setAddingSample(false);
  };

  const handleWishlistToggle = async () => {
    if (isInWishlist(product.id)) {
      await removeFromWishlist(product.id);
      toast.success("Removed from wishlist");
    } else {
      await addToWishlist(product);
      toast.success("Added to wishlist");
    }
  };

  // Share functionality - use current window location for sharing
  const shareUrl = product ? `${window.location.origin}/product/${product.id}` : '';
  const shareProductName = translatedName || (product ? product.name : '');
  const productDescription = product ? encodeURIComponent(`Check out ${shareProductName} at Mini Stripes UK - £${product.price?.toFixed(2)}`) : '';

  const handleShare = (platform) => {
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${productDescription}&url=${encodeURIComponent(shareUrl)}`,
      pinterest: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${productDescription}`,
      whatsapp: `https://wa.me/?text=${productDescription}%20${encodeURIComponent(shareUrl)}`,
    };
    
    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
  };

  const handleEmailFriend = () => {
    const subject = encodeURIComponent(`Check out this product from Mini Stripes UK`);
    const body = encodeURIComponent(`Hi!\n\nI thought you might like this product:\n\n${shareProductName}\nPrice: £${product.price?.toFixed(2)}\n\nView it here: ${productUrl}\n\nBest regards`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    setShowShareMenu(false);
  };

  // Pre-compute SEO values - must be computed before hook call for hook rules
  const seoTitle = product ? (product.meta_title || `${product.name} | Mini Stripes UK`) : 'Mini Stripes UK';
  const seoDescription = product ? (product.meta_description || product.description?.replace(/<[^>]*>/g, '').slice(0, 160) || `Shop ${product.name} at Mini Stripes UK. Premium vinyl graphics for Mini Cooper.`) : '';
  const seoKeywords = product ? (product.meta_keywords || `Mini Cooper, ${product.category_name || ''}, vinyl decals, car graphics UK, ${product.name}`) : '';
  const seoCanonical = product ? `https://ministripes.co.uk/product/${product.seo_slug || product.id}` : '';
  const seoImage = product?.image_url ? (product.image_url.startsWith('http') ? product.image_url : `https://ministripes.co.uk${product.image_url}`) : 'https://ministripes.co.uk/logo.png';
  const productUrl = product ? `https://ministripes.co.uk/product/${product.seo_slug || product.id}` : '';
  
  // Calculate average rating for schema
  const avgRating = reviews.length > 0 ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1) : null;
  
  // Full SEO with Open Graph, Twitter Cards, and Schema via usePageMeta
  usePageMeta({
    title: seoTitle,
    description: seoDescription,
    keywords: seoKeywords,
    canonical: seoCanonical,
    ogTitle: seoTitle,
    ogDescription: seoDescription,
    ogImage: seoImage,
    ogType: 'product',
    twitterCard: 'summary_large_image',
    structuredData: product ? {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.name,
      "description": seoDescription,
      "image": seoImage,
      "url": productUrl,
      "sku": product.id,
      "mpn": product.id,
      "brand": {
        "@type": "Brand",
        "name": "Mini Stripes UK"
      },
      "offers": {
        "@type": "Offer",
        "url": productUrl,
        "priceCurrency": "GBP",
        "price": product.price?.toFixed(2),
        "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "seller": {
          "@type": "Organization",
          "name": "Mini Stripes UK"
        }
      },
      ...(avgRating && reviews.length > 0 ? {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": avgRating,
          "reviewCount": reviews.length,
          "bestRating": "5",
          "worstRating": "1"
        }
      } : {}),
      "category": product.category_name || "Mini Graphics"
    } : null
  });

  if (loading) {
    return (
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="aspect-square bg-secondary animate-pulse" />
            <div className="space-y-4">
              <div className="h-4 bg-secondary animate-pulse w-1/4" />
              <div className="h-10 bg-secondary animate-pulse w-3/4" />
              <div className="h-8 bg-secondary animate-pulse w-1/3" />
              <div className="h-24 bg-secondary animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h1 className="text-2xl font-bold mb-4">Product not found</h1>
          <Button 
            variant="outline" 
            className="rounded-full"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToProducts') || 'Back to Products'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16" data-testid="product-detail-page">

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Back Button */}
        <div className="mb-8">
          <BackButton label={t('backToProducts') || 'Back'} />
        </div>

        {/* Admin Edit Listing Panel */}
        {isAdmin && (
          <div className="mb-8">
            {!isEditing ? (
              <div className="flex gap-2">
                <Button
                  onClick={() => setIsEditing(true)}
                  variant="outline"
                  className="gap-2 border-orange-400 text-orange-600 hover:bg-orange-50"
                  data-testid="admin-edit-listing-btn"
                >
                  <Pencil className="h-4 w-4" />
                  Edit Listing (Admin)
                </Button>
                <Button
                  onClick={handleDuplicateProduct}
                  disabled={duplicating}
                  variant="outline"
                  className="gap-2 border-blue-400 text-blue-600 hover:bg-blue-50"
                  data-testid="admin-duplicate-listing-btn"
                >
                  {duplicating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  {duplicating ? 'Duplicating...' : 'Duplicate Listing'}
                </Button>
                <Button
                  onClick={handleDeleteProduct}
                  disabled={deleting}
                  variant="outline"
                  className="gap-2 border-red-400 text-red-600 hover:bg-red-50"
                  data-testid="admin-delete-listing-btn"
                >
                  {deleting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            ) : (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h3 className="text-lg font-bold text-orange-800 flex items-center gap-2">
                      <Pencil className="h-5 w-5" />
                      Edit Product Listing
                    </h3>
                    {/* Product ID Display */}
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-gray-500">Product ID:</span>
                      <code className="text-xs bg-gray-200 px-2 py-1 rounded font-mono" data-testid="product-id-display">
                        {product.id}
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(product.id);
                          toast.success("Product ID copied to clipboard");
                        }}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                        title="Copy Product ID"
                        data-testid="copy-product-id-btn"
                      >
                        <Copy className="h-3.5 w-3.5 text-gray-500" />
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setIsEditing(false)}
                      variant="outline"
                      size="sm"
                      className="gap-1"
                    >
                      <X className="h-4 w-4" />
                      Cancel
                    </Button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Left Column - Basic Info */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        data-testid="edit-product-name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Price (£)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={editForm.price}
                        onChange={(e) => setEditForm({...editForm, price: parseFloat(e.target.value) || 0})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        data-testid="edit-product-price"
                      />
                    </div>
                    
                    {/* Category Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <select
                        value={editForm.category_id}
                        onChange={(e) => {
                          const selectedCat = categories.find(c => c.id === e.target.value);
                          setEditForm({
                            ...editForm, 
                            category_id: e.target.value,
                            category_name: selectedCat?.name || "",
                            subcategory: selectedCat?.slug || ""
                          });
                        }}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                        data-testid="edit-product-category"
                      >
                        <option value="">Select a category...</option>
                        {categories
                          .filter(c => c.parent_id) // Only show subcategories
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(cat => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                      </select>
                      {editForm.category_name && (
                        <p className="text-xs text-gray-500 mt-1">Current: {editForm.category_name}</p>
                      )}
                    </div>
                    
                    {/* Color Palette Toggle */}
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                      <input
                        type="checkbox"
                        id="has-color-palette"
                        checked={editForm.has_color_palette}
                        onChange={(e) => setEditForm({...editForm, has_color_palette: e.target.checked})}
                        className="w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        data-testid="edit-color-palette-toggle"
                      />
                      <label htmlFor="has-color-palette" className="flex-1">
                        <span className="block text-sm font-medium text-gray-700">Show Color Palette</span>
                        <span className="block text-xs text-gray-500">Enable HEXIS color selection for this product</span>
                      </label>
                      <Palette className="h-5 w-5 text-gray-400" />
                    </div>

                    {/* Dual Color Selection Toggle - Only show if color palette is enabled */}
                    {editForm.has_color_palette && (
                      <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 ml-6">
                        <input
                          type="checkbox"
                          id="requires-two-colors"
                          checked={editForm.requires_two_colors}
                          onChange={(e) => setEditForm({...editForm, requires_two_colors: e.target.checked})}
                          className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          data-testid="edit-dual-color-toggle"
                        />
                        <label htmlFor="requires-two-colors" className="flex-1">
                          <span className="block text-sm font-medium text-gray-700">Requires Two Colours</span>
                          <span className="block text-xs text-gray-500">Customer must select Colour 1 and Colour 2</span>
                        </label>
                        <div className="flex -space-x-1">
                          <div className="w-4 h-4 rounded-full bg-blue-500 border-2 border-white"></div>
                          <div className="w-4 h-4 rounded-full bg-purple-500 border-2 border-white"></div>
                        </div>
                      </div>
                    )}

                    {/* Custom Color Labels - Only show if dual colors is enabled */}
                    {editForm.has_color_palette && editForm.requires_two_colors && (
                      <div className="ml-6 space-y-3 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-gray-200">
                        <p className="text-sm font-medium text-gray-700 mb-2">Custom Colour Labels</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-blue-600 font-medium mb-1">Colour 1 Label</label>
                            <input
                              type="text"
                              value={editForm.color1_label}
                              onChange={(e) => setEditForm({...editForm, color1_label: e.target.value})}
                              placeholder="e.g., Bonnet Colour"
                              className="w-full px-3 py-2 text-sm border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              data-testid="edit-color1-label"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-purple-600 font-medium mb-1">Colour 2 Label</label>
                            <input
                              type="text"
                              value={editForm.color2_label}
                              onChange={(e) => setEditForm({...editForm, color2_label: e.target.value})}
                              placeholder="e.g., Stripe Colour"
                              className="w-full px-3 py-2 text-sm border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                              data-testid="edit-color2-label"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500">These labels explain which part of the product each colour applies to</p>
                      </div>
                    )}

                    {/* Show Materials Section */}
                    <div className="p-3 bg-white rounded-lg border border-gray-200">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="show-material"
                          checked={editForm.show_material}
                          onChange={(e) => setEditForm({...editForm, show_material: e.target.checked})}
                          className="w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <label htmlFor="show-material" className="flex-1">
                          <span className="block text-sm font-medium text-gray-700">Show Materials</span>
                          <span className="block text-xs text-gray-500">Display material type on product</span>
                        </label>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              if (editingMaterialPrices) {
                                setEditingMaterialPrices(false);
                                setMaterialPriceEdits({});
                              } else {
                                setEditingMaterialPrices(true);
                                const priceMap = {};
                                materials.forEach(m => { priceMap[m.id] = m.price_modifier; });
                                setMaterialPriceEdits(priceMap);
                              }
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                          >
                            {editingMaterialPrices ? 'Cancel' : 'Edit Prices'}
                          </button>
                          <Ruler className="h-5 w-5 text-gray-400" />
                        </div>
                      </div>
                      
                      {/* Material Selection or Price Editing */}
                      <div className="mt-3 pl-8">
                        {!editingMaterialPrices ? (
                          <select
                            value={editForm.material_id || ""}
                            onChange={(e) => setEditForm({...editForm, material_id: e.target.value})}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            data-testid="edit-material"
                          >
                            <option value="">Select Material...</option>
                            {materials.map((mat) => (
                              <option key={mat.id} value={mat.id}>
                                {mat.name} {mat.price_modifier !== 0 && `(${mat.price_modifier >= 0 ? '+' : ''}£${mat.price_modifier.toFixed(2)})`}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="space-y-2">
                            <p className="text-xs text-gray-600 font-medium">Edit Material Prices:</p>
                            <div className="max-h-40 overflow-y-auto space-y-2">
                              {materials.map((mat) => (
                                <div key={mat.id} className="flex items-center gap-2 bg-gray-50 p-2 rounded border border-gray-200">
                                  <span className="flex-1 text-xs text-gray-700 truncate" title={mat.name}>
                                    {mat.name}
                                  </span>
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-gray-500">£</span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={materialPriceEdits[mat.id] ?? mat.price_modifier}
                                      onChange={(e) => setMaterialPriceEdits(prev => ({
                                        ...prev,
                                        [mat.id]: e.target.value
                                      }))}
                                      className="w-20 px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-orange-500"
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                            <button
                              type="button"
                              onClick={saveMaterialPrices}
                              disabled={savingMaterials}
                              className="w-full mt-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium rounded-lg disabled:opacity-50"
                            >
                              {savingMaterials ? 'Saving...' : 'Save Material Prices'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Image Upload Section - Multi-Image Support */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Product Images</label>
                      
                      {/* Multi-Image Gallery */}
                      <div className="bg-white rounded-xl p-3 border border-gray-200 mb-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-600">
                            Image Order ({(() => {
                              const imgs = product?.images || [];
                              if (imgs.length > 0) return imgs.length;
                              return product?.image_url ? 1 : 0;
                            })()})
                          </span>
                          <span className="text-xs text-orange-600 font-medium">
                            #1 = Main image
                          </span>
                        </div>
                        
                        {(() => {
                          // Use images array if available, otherwise fallback to image_url
                          const displayImages = (product?.images && product.images.length > 0) 
                            ? product.images 
                            : (product?.image_url ? [product.image_url] : []);
                          
                          return displayImages.length > 0 ? (
                          <>
                            <div className="grid grid-cols-5 gap-2">
                              {displayImages.map((img, idx) => {
                                const authToken = localStorage.getItem('auth_token');
                                
                                const handleDelete = async () => {
                                  const confirmDelete = window.confirm("Delete this image?");
                                  if (!confirmDelete) return;
                                  
                                  const token = localStorage.getItem('auth_token');
                                  if (!token) {
                                    toast.error("Please login to delete images");
                                    return;
                                  }
                                  
                                  try {
                                    const response = await axios.delete(
                                      `${API}/products/${product.id}/images/${idx}`,
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    const updated = response.data.images || [];
                                    setProduct(prev => ({...prev, images: updated, image_url: updated[0] || ""}));
                                    setEditForm(prev => ({...prev, image_url: updated[0] || ""}));
                                    toast.success("Image deleted successfully");
                                  } catch (error) {
                                    console.error("Failed to delete image:", error);
                                    toast.error("Failed to delete: " + (error.response?.data?.detail || error.message));
                                  }
                                };
                                
                                return (
                                <div 
                                  key={img + idx} 
                                  className="relative"
                                >
                                  {/* Order Number */}
                                  <div className={`absolute -top-1 -left-1 z-10 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shadow ${idx === 0 ? 'bg-orange-500 text-white' : 'bg-white text-gray-700 border border-gray-300'}`}>
                                    {idx + 1}
                                  </div>
                                  
                                  {/* Image Container */}
                                  <div 
                                    draggable
                                    onDragStart={(e) => {
                                      e.dataTransfer.setData('text/plain', idx.toString());
                                      e.currentTarget.style.opacity = '0.5';
                                    }}
                                    onDragEnd={(e) => {
                                      e.currentTarget.style.opacity = '1';
                                    }}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.currentTarget.style.transform = 'scale(1.05)';
                                    }}
                                    onDragLeave={(e) => {
                                      e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                    onDrop={async (e) => {
                                      e.preventDefault();
                                      e.currentTarget.style.transform = 'scale(1)';
                                      const fromIdx = parseInt(e.dataTransfer.getData('text/plain'));
                                      const toIdx = idx;
                                      if (fromIdx === toIdx) return;
                                      
                                      const images = [...displayImages];
                                      const [movedItem] = images.splice(fromIdx, 1);
                                      images.splice(toIdx, 0, movedItem);
                                      
                                      try {
                                        await axios.put(
                                          `${API}/products/${product.id}/images/reorder`,
                                          images,
                                          { headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' } }
                                        );
                                        setProduct({...product, images, image_url: images[0] || ""});
                                        setEditForm({...editForm, image_url: images[0] || ""});
                                        toast.success("Images reordered successfully");
                                      } catch (error) {
                                        console.error("Failed to reorder images:", error);
                                        toast.error("Failed to reorder images");
                                      }
                                    }}
                                    className={`aspect-square rounded-lg overflow-hidden cursor-grab active:cursor-grabbing ${idx === 0 ? 'ring-2 ring-orange-500' : 'border border-gray-200'}`}
                                  >
                                    <img src={img} alt={`Image ${idx + 1}`} className="w-full h-full object-cover pointer-events-none" />
                                  </div>
                                  
                                  {/* DELETE LINK - Completely separate from drag */}
                                  <div className="text-center mt-1">
                                    <span 
                                      onClick={handleDelete}
                                      className="text-red-500 hover:text-red-700 text-xs cursor-pointer underline"
                                    >
                                      Delete
                                    </span>
                                  </div>
                                </div>
                              )})}
                              
                              {/* Add Image Button */}
                              <label className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-orange-400 cursor-pointer flex flex-col items-center justify-center text-gray-400 hover:text-orange-500 transition-colors bg-gray-50">
                                <input
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp,image/gif"
                                  multiple
                                  className="hidden"
                                  onChange={async (e) => {
                                    const files = e.target.files;
                                    if (!files || files.length === 0 || !product) return;
                                    
                                    const authToken = localStorage.getItem('auth_token');
                                    setUploadingImage(true);
                                    const formData = new FormData();
                                    
                                    // Append all selected files
                                    for (let i = 0; i < files.length; i++) {
                                      formData.append('files', files[i]);
                                    }
                                    
                                    try {
                                      const response = await axios.post(
                                        `${API}/products/${product.id}/add-images`,
                                        formData,
                                        { headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' } }
                                      );
                                      setProduct({...product, images: response.data.images, image_url: response.data.images[0] || product.image_url});
                                      setEditForm({...editForm, image_url: response.data.images[0] || editForm.image_url});
                                      toast.success(`${response.data.added_count} image(s) uploaded successfully!`);
                                    } catch (error) {
                                      console.error("Failed to add images");
                                      toast.error("Failed to upload images");
                                    } finally {
                                      setUploadingImage(false);
                                      e.target.value = ''; // Reset input
                                    }
                                  }}
                                />
                                {uploadingImage ? (
                                  <Loader2 className="h-5 w-5 animate-spin" />
                                ) : (
                                  <>
                                    <Plus className="h-5 w-5" />
                                    <span className="text-[9px] mt-0.5">Add</span>
                                  </>
                                )}
                              </label>
                            </div>
                            <p className="text-xs text-gray-500 mt-2 text-center">
                              Drag to reorder • Hover to delete • #1 is main
                            </p>
                          </>
                        ) : (
                          <div className="text-center py-4">
                            <ImageIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                            <p className="text-xs text-gray-500 mb-2">No images yet</p>
                            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-lg cursor-pointer transition-colors text-sm">
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                multiple
                                className="hidden"
                                onChange={async (e) => {
                                  const files = e.target.files;
                                  if (!files || files.length === 0 || !product) return;
                                  
                                  const authToken = localStorage.getItem('auth_token');
                                  setUploadingImage(true);
                                  const formData = new FormData();
                                  
                                  for (let i = 0; i < files.length; i++) {
                                    formData.append('files', files[i]);
                                  }
                                  
                                  try {
                                    const response = await axios.post(
                                      `${API}/products/${product.id}/add-images`,
                                      formData,
                                      { headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' } }
                                    );
                                    setProduct({...product, images: response.data.images, image_url: response.data.image_url});
                                    setEditForm({...editForm, image_url: response.data.image_url});
                                    toast.success(`${response.data.added_count} image(s) uploaded successfully!`);
                                  } catch (error) {
                                    console.error("Failed to add images");
                                    toast.error("Failed to upload images");
                                  } finally {
                                    setUploadingImage(false);
                                    e.target.value = '';
                                  }
                                }}
                              />
                              {uploadingImage ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Upload className="h-4 w-4" />
                              )}
                              Upload Images
                            </label>
                          </div>
                        );
                        })()}
                      </div>
                      
                      {/* Manual URL Input */}
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Or enter image URL</label>
                        <input
                          type="text"
                          value={editForm.image_url}
                          onChange={(e) => setEditForm({...editForm, image_url: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                          placeholder="Enter image URL manually..."
                          data-testid="edit-product-image-url"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Max 5MB. JPEG, PNG, WebP, GIF supported.</p>
                    </div>
                  </div>

                  {/* Right Column - Google SEO */}
                  <div className="space-y-4">
                    {/* Google SEO */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Search className="h-4 w-4 text-blue-600" />
                        <h4 className="text-sm font-bold text-gray-800">Google Search Preview</h4>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Page Title <span className="text-xs text-gray-400">({editForm.meta_title?.length || 0}/60)</span>
                        </label>
                        <input
                          type="text"
                          value={editForm.meta_title}
                          onChange={(e) => setEditForm({...editForm, meta_title: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Title shown in Google search results"
                          data-testid="edit-seo-title"
                          maxLength={60}
                        />
                      </div>
                      
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description <span className="text-xs text-gray-400">({editForm.meta_description?.length || 0}/160)</span>
                        </label>
                        <textarea
                          value={editForm.meta_description}
                          onChange={(e) => setEditForm({...editForm, meta_description: e.target.value})}
                          rows={2}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                          placeholder="Description shown under the title in Google"
                          data-testid="edit-seo-description"
                          maxLength={160}
                        />
                      </div>
                      
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Search Friendly Page Name
                        </label>
                        <div className="flex items-center">
                          <span className="text-xs text-gray-400 mr-1">/product/</span>
                          <input
                            type="text"
                            value={editForm.seo_slug}
                            onChange={(e) => setEditForm({...editForm, seo_slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')})}
                            className="flex-1 px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="black-matte-vinyl-decal"
                            data-testid="edit-seo-slug"
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Clean URL for better SEO</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Full Width Rich Text Description Editor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Product Description (Rich Text)</label>
                  <div className="bg-white rounded-lg border border-gray-300 overflow-hidden" data-testid="edit-product-description-editor">
                    <Editor
                      tinymceScriptSrc="/tinymce/tinymce.min.js"
                      value={editForm.description}
                      onEditorChange={(content) => setEditForm({...editForm, description: content})}
                      init={tinymceInit}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Use the toolbar to add formatting, links, images, tables and more.</p>
                </div>

                {/* Save Button - Under Description */}
                <div className="flex justify-end pt-4">
                  <Button
                    onClick={handleSaveProduct}
                    disabled={saving}
                    size="lg"
                    className="gap-2 bg-orange-600 hover:bg-orange-700 px-8"
                  >
                    {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
                    Save Changes
                  </Button>
                </div>

                {/* Related Products Section */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <Package className="h-5 w-5 text-blue-600" />
                        Related Products
                      </h3>
                      <p className="text-sm text-gray-500">Select products to show as related items</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        fetchAllProducts();
                        setShowRelatedPicker(true);
                      }}
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Related
                    </Button>
                  </div>

                  {/* Selected Related Products */}
                  {editForm.related_products && editForm.related_products.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {editForm.related_products.map((relatedId, idx) => {
                        const relatedProduct = allProducts.find(p => p.id === relatedId) || 
                          { id: relatedId, name: "Loading...", image_url: "" };
                        return (
                          <div 
                            key={relatedId} 
                            className="relative bg-white rounded-lg border p-2 group hover:shadow-md transition-shadow"
                          >
                            <button
                              type="button"
                              onClick={() => {
                                setEditForm({
                                  ...editForm,
                                  related_products: editForm.related_products.filter(id => id !== relatedId)
                                });
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                              title="Remove"
                            >
                              <X className="h-3 w-3" />
                            </button>
                            <div className="aspect-square bg-gray-100 rounded overflow-hidden mb-2">
                              {relatedProduct.image_url ? (
                                <img
                                  src={relatedProduct.image_url}
                                  alt={relatedProduct.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => { e.target.src = "https://via.placeholder.com/100?text=No+Image"; }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <ImageIcon className="h-8 w-8" />
                                </div>
                              )}
                            </div>
                            <p className="text-xs font-medium text-gray-700 truncate" title={relatedProduct.name}>
                              {relatedProduct.name}
                            </p>
                            {relatedProduct.price && (
                              <p className="text-xs text-gray-500">£{relatedProduct.price?.toFixed(2)}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-white rounded-lg border border-dashed border-gray-300">
                      <Package className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No related products selected</p>
                      <p className="text-xs text-gray-400">Click "Add Related" to select products</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-12">
          {/* Left Column - Image and Description */}
          <div className="space-y-6">
            {/* Product Image Gallery */}
            {(() => {
              // Check if image is a valid product-specific image (not a category placeholder)
              // Only block specific category placeholder patterns, not product names that contain category words
              const isProductImage = (url) => {
                if (!url) return false;
                // These are specific category placeholder image patterns (with underscores/specific formats)
                const categoryPlaceholderPatterns = [
                  'classic-mini-1959-1986_',
                  'mini-cooper-gen-1-2002-2006_',
                  'mini-cooper-gen-2-2007-2013_', 
                  'mini-cooper-gen-3-2014-early-2024_',
                  'mini-cooper-gen-4-2024-onwards_',
                  'mini-gp-gen-1-gen-2-gen-3_',
                  'mini-countryman-all-generations_',
                  'mini-clubman-all-generations_'
                ];
                const lowerUrl = url.toLowerCase();
                return !categoryPlaceholderPatterns.some(pattern => lowerUrl.includes(pattern));
              };
              
              // Build images array
              const allImages = [];
              if (product.image_url && isProductImage(product.image_url)) {
                allImages.push(product.image_url);
              }
              if (product.images && Array.isArray(product.images)) {
                product.images.forEach(img => {
                  if (img && !allImages.includes(img)) {
                    allImages.push(img);
                  }
                });
              }
              
              const hasValidImages = allImages.length > 0 && !imageError;
              const currentImage = hasValidImages ? allImages[selectedImageIndex] || allImages[0] : null;
              
              return (
                <>
                  {/* Main Image with Click to Enlarge */}
                  <div 
                    className="aspect-square bg-secondary overflow-hidden relative group cursor-pointer"
                    onClick={() => hasValidImages && setLightboxOpen(true)}
                  >
                    {hasValidImages ? (
                      <>
                        <img
                          src={currentImage}
                          alt={product.name}
                          className="w-full h-full object-contain"
                          onError={() => setImageError(true)}
                          data-testid="product-main-image"
                        />
                        {/* Zoom icon overlay */}
                        <div className="absolute top-3 right-3 p-2 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <ZoomIn className="w-5 h-5" />
                        </div>
                        <div className="absolute bottom-3 right-3 text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/80 px-2 py-1 rounded">
                          Click to enlarge
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-200">
                        <span 
                          className="text-gray-500 text-xl font-medium tracking-wide"
                          style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
                        >
                          Awaiting Image
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Image Lightbox Modal */}
                  <ImageLightbox
                    images={allImages}
                    initialIndex={selectedImageIndex}
                    alt={product.name}
                    isOpen={lightboxOpen}
                    onClose={() => setLightboxOpen(false)}
                  />
                  
                  {/* Thumbnail Gallery */}
                  {allImages.length > 1 && (
                    <div className="flex gap-3 mt-4" data-testid="product-thumbnails">
                      {allImages.map((img, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedImageIndex(index)}
                          className={`w-20 h-20 border-2 overflow-hidden transition-all ${
                            selectedImageIndex === index 
                              ? 'border-foreground' 
                              : 'border-transparent hover:border-muted-foreground'
                          }`}
                          data-testid={`thumbnail-${index}`}
                        >
                          <img
                            src={img}
                            alt={`${product.name} - Image ${index + 1}`}
                            className="w-full h-full object-contain"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Product ID - Below Image */}
                  <p className="text-xs text-gray-400 mt-4 font-mono">
                    Product ID: {product.id}
                  </p>
                </>
              );
            })()}

            {/* Product Description - Below Image */}
            <div 
              className="text-foreground leading-snug text-base font-medium prose prose-sm max-w-none whitespace-pre-line [&_p]:mb-2 [&_ul]:mt-1 [&_ul]:mb-3 [&_li]:mb-0.5"
              dangerouslySetInnerHTML={{ 
                __html: translatedDescription || product.description || "Premium quality vinyl decal. Easy to apply with included instructions." 
              }}
              data-testid="product-description"
            />
          </div>

          {/* Right Column - Product Info */}
          <div className="space-y-8">
            {/* Product Title */}
            <div>
              <h1 
                className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900"
                data-testid="product-title"
              >
                {translatedName || product.name}
              </h1>
            </div>

            {/* Price - with price increases for premium vinyl series and material */}
            {(() => {
              const isSkintacColor = selectedHexisColor?.series === "HX20000";
              const isSuptacHXColor = selectedHexisColor?.series === "HXS5000";
              let displayPrice = product.price;
              let priceMessage = "";
              
              // Apply HEXIS premium pricing
              if (isSkintacColor) {
                displayPrice = product.price * 1.5;
                priceMessage = "Premium Skintac vinyl selected (+50%)";
              } else if (isSuptacHXColor) {
                displayPrice = product.price * 1.4;
                priceMessage = "Premium Suptac HX Air release vinyl selected (+40%)";
              }
              
              // Add material price modifier
              const materialModifier = customerSelectedMaterial?.price_modifier || 0;
              
              // Add application tools total
              const toolsTotal = selectedTools.reduce((sum, tool) => sum + (tool.price || 0), 0);
              
              const finalPrice = displayPrice + materialModifier + toolsTotal;
              
              return (
                <div className="pb-6 border-b border-gray-200">
                  <p className="price-font text-4xl font-bold text-gray-900">
                    {formatPrice(finalPrice)}
                  </p>
                  {(isSkintacColor || isSuptacHXColor || materialModifier !== 0 || toolsTotal > 0) && (
                    <div className="mt-2 space-y-1">
                      {(isSkintacColor || isSuptacHXColor) && (
                        <p className="text-sm text-gray-500">
                          <span className="line-through">{formatPrice(product.price)}</span>
                          <span className="ml-2 text-green-600 font-medium">{priceMessage}</span>
                        </p>
                      )}
                      {materialModifier !== 0 && (
                        <p className="text-sm text-gray-500">
                          <span className="text-orange-600 font-medium">
                            Material: {customerSelectedMaterial?.name} ({materialModifier >= 0 ? '+' : ''}£{materialModifier.toFixed(2)})
                          </span>
                        </p>
                      )}
                      {toolsTotal > 0 && (
                        <p className="text-sm text-gray-500">
                          <span className="text-blue-600 font-medium">
                            Application Tools: {selectedTools.length} item{selectedTools.length > 1 ? 's' : ''} (+£{toolsTotal.toFixed(2)})
                          </span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Color/Finish Options */}
            {product.color_options && product.color_options.length > 0 && (
              <div className="space-y-4">
                {/* Check if it's a PPF product (finish options) or regular product (color options) */}
                {product.name?.includes('PPF') ? (
                  <>
                    <label className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                      Finish: <span className="text-gray-600 font-normal normal-case">{selectedColor}</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {product.color_options.map((finish) => (
                        <button
                          key={finish}
                          onClick={() => {
                            setSelectedColor(finish);
                            setSelectedHexisColor(null);
                          }}
                          className={`px-5 py-2.5 text-sm font-medium rounded-lg border-2 transition-all ${
                            selectedColor === finish && !selectedHexisColor
                              ? 'border-black bg-black text-white'
                              : 'border-gray-200 hover:border-gray-400 bg-white text-gray-700'
                          }`}
                          data-testid={`finish-${finish.toLowerCase().replace(/\s/g, '-')}`}
                        >
                          {finish}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    {/* ============================================
                        QUICK COLOR OPTIONS - HIDDEN BUT PRESERVED
                        Set showQuickColors to true to re-enable
                        ============================================ */}
                    {false && /* showQuickColors */ (
                    <div className="flex flex-wrap gap-2">
                      {product.color_options.map((color) => {
                        const colorMap = {
                          'Favoured Gold - Air release Vinyl': '#b39e7a',
                          'Chili Red - Air release Vinyl': '#ad0000',
                          'Canary Yellow': '#fcff29',
                          'Sunflower Yellow': '#f0d100',
                          'Orange': '#eb9600',
                          'Dark Orange': '#e9770c',
                          'Red': '#fe2410',
                          'Tomatoe Red': '#ff551f',
                          'Burgundy': '#990008',
                          'Baby Pink': '#fdbff4',
                          'Hot Pink': '#ef06a5',
                          'Lilac': '#e4bae8',
                          'Purple': '#9b29ff',
                          'Kawasaki Green': '#6ddc28',
                          'Grass Green': '#13963a',
                          'Dark Green': '#0a4d00',
                          'Mint Green': '#b0fdd2',
                          'Turquoise': '#67b4bc',
                          'Baby Blue': '#00b8d6',
                          'Sky Blue': '#c1f3f6',
                          'Mid Blue': '#0066fa',
                          'Ultramarine Blue': '#1100c2',
                          'Navy Blue': '#0b0070',
                          'Beige': '#eae6cd',
                          'Light Grey': '#d9d9d9',
                          'Dark Grey': '#6b6b6b',
                          'Brown': '#6c482d',
                          'Black': '#000000',
                          'White': '#ffffff',
                          'Gold': '#b5a94f',
                          'Metallic Anthracite Grey': '#333333',
                          'Matte Metallic Anthracite Grey': '#333333',
                          'Silver': '#cccccc',
                          'Matte Black': '#000000',
                          'Matte Red': '#eb1900',
                          'Matte White': '#ffffff',
                          'Glass Frosted / Etch': '#e6e6e6',
                          'Carbon Fibre': '#454545'
                        };
                        const bgColor = colorMap[color] || '#cccccc';
                        const isLight = ['Canary Yellow', 'Sunflower Yellow', 'Baby Pink', 'Lilac', 'Mint Green', 'Sky Blue', 'Beige', 'Light Grey', 'White', 'Matte White', 'Glass Frosted / Etch'].includes(color);
                        
                        return (
                          <button
                            key={color}
                            onClick={() => {
                              setSelectedColor(color);
                              setSelectedHexisColor(null);
                            }}
                            className={`flex items-center gap-2 px-3 py-2 text-sm border transition-all ${
                              selectedColor === color && !selectedHexisColor
                                ? 'border-black ring-2 ring-black ring-offset-1'
                                : 'border-gray-300 hover:border-black'
                            }`}
                            data-testid={`color-${color.toLowerCase().replace(/\s/g, '-')}`}
                          >
                            <span 
                              className={`w-6 h-6 rounded-full border ${isLight ? 'border-gray-300' : 'border-transparent'}`}
                              style={{ backgroundColor: bgColor }}
                            />
                            <span>{color}</span>
                          </button>
                        );
                      })}
                    </div>
                    )}
                    {/* END OF HIDDEN QUICK COLOR OPTIONS */}
                    
                    {/* Material Selector - Shows independently based on show_material setting */}
                    {product.show_material && materials.length > 0 && (
                      <div className="flex flex-wrap gap-3 pt-3">
                        <div className="relative">
                          <select
                            value={customerSelectedMaterial?.id || ""}
                            onChange={(e) => {
                              const selected = materials.find(m => m.id === e.target.value);
                              setCustomerSelectedMaterial(selected || null);
                            }}
                            className="appearance-none flex items-center gap-2 px-6 py-3 pr-10 text-sm font-bold rounded-lg transition-all bg-white text-gray-900 border-2 border-gray-300 hover:border-gray-400 cursor-pointer"
                            data-testid="material-selector"
                          >
                            <option value="">Select Material</option>
                            {materials.map((mat) => (
                              <option key={mat.id} value={mat.id}>
                                {mat.name} {mat.price_modifier !== 0 ? `(${mat.price_modifier >= 0 ? '+' : ''}£${mat.price_modifier.toFixed(2)})` : ''}
                              </option>
                            ))}
                          </select>
                          <Ruler className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                        </div>
                      </div>
                    )}
                    
                    {/* HEXIS Color Palette Toggle - Rainbow Tab (only shown if has_color_palette is true) */}
                    {product.has_color_palette !== false && !isWrapVinylProduct && (
                    <div className="flex flex-wrap gap-3 pt-3">
                      <button
                        onClick={() => {
                          setSampleMode(false);
                          setPendingHexisColor(null);
                          setShowHexisPalette(!showHexisPalette);
                        }}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-bold transition-all rounded-lg border-2 ${
                          (showHexisPalette && !sampleMode) || selectedHexisColor
                            ? 'bg-black text-white border-black'
                            : 'text-gray-900 border-gray-300 hover:border-black'
                        }`}
                        style={{
                          background: (showHexisPalette && !sampleMode) || selectedHexisColor
                            ? '#000000'
                            : 'linear-gradient(90deg, #ff0000 0%, #ff8000 14%, #ffff00 28%, #00ff00 42%, #00ffff 57%, #0080ff 71%, #8000ff 85%, #ff0080 100%)',
                        }}
                        data-testid="hexis-palette-toggle"
                      >
                        <Palette className="h-4 w-4" />
                        {showHexisPalette && !sampleMode ? 'Hide HEXIS Palette' : 'Browse 216 HEXIS Colours'}
                      </button>
                      
                      {/* Request A5 Sample Colour Button */}
                      <button
                        onClick={() => {
                          setSampleMode(true);
                          setShowHexisPalette(true);
                          setSampleColor(null);
                        }}
                        className="flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-lg transition-all bg-black text-white border-2 border-black hover:bg-gray-800"
                      >
                        <span>Request A5 Sample</span>
                        <span className="bg-white text-black px-2 py-0.5 rounded text-xs font-bold">£4.99</span>
                      </button>
                    </div>
                    )}
                    
                    {/* A5 Sample Selection Display (only shown if has_color_palette is true) */}
                    {product.has_color_palette !== false && !isWrapVinylProduct && sampleMode && sampleColor && !showHexisPalette && (
                      <div className="mt-4 p-4 bg-amber-50 rounded-xl border-2 border-amber-300">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg border-2 border-gray-300 flex-shrink-0 relative overflow-hidden shadow-md">
                            <div 
                              className="absolute inset-0"
                              style={{ backgroundColor: sampleColor.hex }}
                            />
                            <div 
                              className="absolute inset-0"
                              style={{
                                background: `linear-gradient(145deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.15) 25%, rgba(255,255,255,0) 50%, rgba(0,0,0,0.05) 75%, rgba(0,0,0,0.15) 100%)`,
                              }}
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-amber-700 font-medium uppercase tracking-wider">A5 Sample Selected</p>
                            <p className="font-bold text-base truncate">{sampleColor.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {sampleColor.code} • A5 (148 x 210mm) • £4.99
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setSampleMode(true);
                                setShowHexisPalette(true);
                              }}
                              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                            >
                              Change
                            </button>
                            <button
                              onClick={handleAddSampleToCart}
                              disabled={addingSample}
                              className="px-4 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors disabled:opacity-50"
                            >
                              {addingSample ? 'Adding...' : 'Add Sample £4.99'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Selected HEXIS Color Display - Below Browse Button (only shown if has_color_palette is true) */}
                    {product.has_color_palette !== false && !isWrapVinylProduct && (selectedHexisColor || selectedHexisColor2) && !showHexisPalette && !sampleMode && (
                      <div className="space-y-3 mt-3">
                        {/* Colour 1 Display */}
                        {selectedHexisColor && (
                          <div className={`flex items-center gap-3 p-4 rounded-lg border-2 ${product.requires_two_colors ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                            <div className="w-12 h-12 rounded-lg border border-gray-300 flex-shrink-0 relative overflow-hidden shadow-md">
                              <div 
                                className="absolute inset-0"
                                style={{ backgroundColor: selectedHexisColor.hex }}
                              />
                              <div 
                                className="absolute inset-0"
                                style={{
                                  background: `linear-gradient(145deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.15) 25%, rgba(255,255,255,0) 50%, rgba(0,0,0,0.05) 75%, rgba(0,0,0,0.15) 100%)`,
                                }}
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className={`text-xs font-medium uppercase tracking-wider ${product.requires_two_colors ? 'text-blue-700' : 'text-green-700'}`}>
                                {product.requires_two_colors ? (product.color1_label || 'Colour 1') : 'Selected Colour'}
                              </p>
                              <p className="font-bold text-base truncate">{selectedHexisColor.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {selectedHexisColor.code}
                                {selectedHexisColor.series && ` • ${selectedHexisColor.series}`}
                              </p>
                            </div>
                            <button
                              onClick={() => {
                                setActiveColorSlot(1);
                                setSelectedHexisColor(null);
                              }}
                              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                            >
                              Change
                            </button>
                          </div>
                        )}
                        
                        {/* Colour 2 Display - Only for dual color products */}
                        {product.requires_two_colors && selectedHexisColor2 && (
                          <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
                            <div className="w-12 h-12 rounded-lg border border-gray-300 flex-shrink-0 relative overflow-hidden shadow-md">
                              <div 
                                className="absolute inset-0"
                                style={{ backgroundColor: selectedHexisColor2.hex }}
                              />
                              <div 
                                className="absolute inset-0"
                                style={{
                                  background: `linear-gradient(145deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.15) 25%, rgba(255,255,255,0) 50%, rgba(0,0,0,0.05) 75%, rgba(0,0,0,0.15) 100%)`,
                                }}
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs text-purple-700 font-medium uppercase tracking-wider">{product.color2_label || 'Colour 2'}</p>
                              <p className="font-bold text-base truncate">{selectedHexisColor2.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {selectedHexisColor2.code}
                                {selectedHexisColor2.series && ` • ${selectedHexisColor2.series}`}
                              </p>
                            </div>
                            <button
                              onClick={() => {
                                setActiveColorSlot(2);
                                setSelectedHexisColor2(null);
                              }}
                              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                            >
                              Change
                            </button>
                          </div>
                        )}
                        
                        {/* Add Colour 2 Button - Only show for dual color products when colour 1 is selected but colour 2 is not */}
                        {product.requires_two_colors && selectedHexisColor && !selectedHexisColor2 && (
                          <button
                            onClick={() => {
                              setActiveColorSlot(2);
                              setShowHexisPalette(true);
                            }}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-lg border-2 border-dashed border-purple-300 text-purple-600 hover:bg-purple-50 transition-colors"
                          >
                            <Plus className="h-4 w-4" />
                            Select {product.color2_label || 'Colour 2'}
                          </button>
                        )}
                      </div>
                    )}
                    
                    {/* HEXIS Color Palette Panel (only shown if has_color_palette is true) */}
                    {product.has_color_palette !== false && !isWrapVinylProduct && showHexisPalette && (
                      <div className="mt-3 p-4 border-2 border-black rounded-lg bg-white">
                        {/* Dual Color Slot Indicator */}
                        {product.requires_two_colors && (
                          <div className="mb-4 p-3 bg-gray-100 rounded-lg border border-gray-300">
                            <p className="text-sm font-medium text-gray-800">
                              Selecting: <span className={activeColorSlot === 1 ? 'text-blue-600 font-bold' : 'text-purple-600 font-bold'}>
                                {activeColorSlot === 1 ? (product.color1_label || 'Colour 1') : (product.color2_label || 'Colour 2')}
                              </span>
                            </p>
                          </div>
                        )}
                        {sampleMode && (
                          <div className="mb-4 p-3 bg-gray-100 rounded-lg border border-gray-300">
                            <p className="text-sm font-medium text-gray-800">
                              Select a colour for your A5 Sample Swatch (148 x 210mm) - £4.99
                            </p>
                          </div>
                        )}
                        <HexisColorPalette
                          onSelectColor={(color) => {
                            if (sampleMode) {
                              setSampleColor(color);
                              setShowHexisPalette(false);
                              toast.success(`Sample colour selected: ${color.name}`, {
                                description: `Click "Add Sample £4.99" to add to cart`
                              });
                              // Scroll back to product info after selection
                              setTimeout(() => {
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }, 100);
                            } else {
                              // Set as pending color - don't close menu yet
                              setPendingHexisColor(color);
                              const labelText = product.requires_two_colors 
                                ? (activeColorSlot === 1 ? (product.color1_label || 'Colour 1') : (product.color2_label || 'Colour 2'))
                                : '';
                              toast.info(`${color.name} selected`, {
                                description: `Click "Confirm ${labelText}" to apply`
                              });
                            }
                          }}
                          selectedColor={sampleMode ? sampleColor : (pendingHexisColor || (activeColorSlot === 1 ? selectedHexisColor : selectedHexisColor2))}
                          showAllSeries={true}
                        />
                        
                        {/* Confirm Colour Button - Only show when a pending color is selected */}
                        {!sampleMode && pendingHexisColor && (
                          <div className="sticky bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-lg">
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-3">
                                <div 
                                  className="w-10 h-10 rounded-lg border-2 border-gray-300 shadow-md"
                                  style={{ backgroundColor: pendingHexisColor.hex }}
                                />
                                <div>
                                  <p className="font-bold text-sm">{pendingHexisColor.name}</p>
                                  <p className="text-xs text-gray-500">{pendingHexisColor.code}</p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setPendingHexisColor(null)}
                                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => {
                                    // Handle dual color selection
                                    if (product.requires_two_colors && activeColorSlot === 2) {
                                      setSelectedHexisColor2(pendingHexisColor);
                                      toast.success(`${product.color2_label || 'Colour 2'} Confirmed: ${pendingHexisColor.name}`, {
                                        description: `${pendingHexisColor.code} - ${pendingHexisColor.series || 'HEXIS'}`
                                      });
                                    } else {
                                      setSelectedHexisColor(pendingHexisColor);
                                      setSelectedColor(null);
                                      const label = product.requires_two_colors ? (product.color1_label || 'Colour 1') : 'Colour';
                                      toast.success(`${label} Confirmed: ${pendingHexisColor.name}`, {
                                        description: `${pendingHexisColor.code} - ${pendingHexisColor.series || 'HEXIS'} ${pendingHexisColor.finish || ''}`
                                      });
                                    }
                                    setPendingHexisColor(null);
                                    setShowHexisPalette(false);
                                    setActiveColorSlot(1); // Reset to slot 1
                                    // Scroll back to product info after confirmation
                                    setTimeout(() => {
                                      window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }, 100);
                                  }}
                                  className={`px-6 py-2 text-sm text-white rounded-lg transition-colors font-semibold ${
                                    product.requires_two_colors && activeColorSlot === 2 
                                      ? 'bg-purple-600 hover:bg-purple-700' 
                                      : 'bg-green-600 hover:bg-green-700'
                                  }`}
                                >
                                  Confirm {product.requires_two_colors ? (activeColorSlot === 1 ? (product.color1_label || 'Colour 1') : (product.color2_label || 'Colour 2')) : 'Colour'}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Material Selector - Standalone for products without color_options (independent of color palette) */}
            {(!product.color_options || product.color_options.length === 0) && product.show_material && materials.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-3">
                <div className="relative">
                  <select
                    value={customerSelectedMaterial?.id || ""}
                    onChange={(e) => {
                      const selected = materials.find(m => m.id === e.target.value);
                      setCustomerSelectedMaterial(selected || null);
                    }}
                    className="appearance-none flex items-center gap-2 px-5 py-2.5 pr-10 text-sm font-semibold rounded-full transition-all bg-white text-gray-900 border-2 border-gray-300 hover:border-gray-400 cursor-pointer"
                    data-testid="material-selector-no-color"
                  >
                    <option value="">Select Material</option>
                    {materials.map((mat) => (
                      <option key={mat.id} value={mat.id}>
                        {mat.name} {mat.price_modifier !== 0 ? `(${mat.price_modifier >= 0 ? '+' : ''}£${mat.price_modifier.toFixed(2)})` : ''}
                      </option>
                    ))}
                  </select>
                  <Ruler className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                </div>
              </div>
            )}

            {/* HEXIS Palette for products without color_options (only shown if has_color_palette is true and NOT wrap vinyl) */}
            {(!product.color_options || product.color_options.length === 0) && product.has_color_palette !== false && !isWrapVinylProduct && (
              <div className="space-y-3">
                {/* HEXIS Color Palette Toggle and Sample Request - Rainbow Tab */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setSampleMode(false);
                      setPendingHexisColor(null);
                      setShowHexisPalette(!showHexisPalette);
                    }}
                    className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold transition-all rounded-full border-2 border-black ${
                      (showHexisPalette && !sampleMode) || selectedHexisColor
                        ? 'text-white'
                        : 'text-black hover:scale-105'
                    }`}
                    style={{
                      background: (showHexisPalette && !sampleMode) || selectedHexisColor
                        ? '#000000'
                        : 'linear-gradient(90deg, #ff0000 0%, #ff8000 14%, #ffff00 28%, #00ff00 42%, #00ffff 57%, #0080ff 71%, #8000ff 85%, #ff0080 100%)',
                      boxShadow: (showHexisPalette && !sampleMode) || selectedHexisColor
                        ? 'none'
                        : '0 2px 8px rgba(0,0,0,0.15)',
                    }}
                    data-testid="hexis-palette-toggle"
                  >
                    <Palette className="h-4 w-4" />
                    {showHexisPalette && !sampleMode ? 'Hide HEXIS Palette' : 'Select from 216 HEXIS Vinyl Colours'}
                  </button>
                  
                  {/* Request A5 Sample Colour Button - Always Black */}
                  <button
                    onClick={() => {
                      setSampleMode(true);
                      setShowHexisPalette(true);
                      setSampleColor(null);
                    }}
                    className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-full transition-all bg-black text-white border-2 border-black hover:bg-gray-800"
                  >
                    <span>Request A5 Sample Colour</span>
                    <span className="font-bold">£4.99</span>
                  </button>
                </div>
                
                {/* A5 Sample Selection Display */}
                {sampleMode && sampleColor && !showHexisPalette && (
                  <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-300">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg border border-gray-300 flex-shrink-0 relative overflow-hidden shadow-md">
                        <div 
                          className="absolute inset-0"
                          style={{ backgroundColor: sampleColor.hex }}
                        />
                        <div 
                          className="absolute inset-0"
                          style={{
                            background: `linear-gradient(145deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.15) 25%, rgba(255,255,255,0) 50%, rgba(0,0,0,0.05) 75%, rgba(0,0,0,0.15) 100%)`,
                          }}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-amber-700 font-medium uppercase tracking-wider">A5 Sample Selected</p>
                        <p className="font-bold text-base truncate">{sampleColor.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {sampleColor.code} • A5 (148 x 210mm) • £4.99
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setSampleMode(true);
                            setShowHexisPalette(true);
                          }}
                          className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                        >
                          Change
                        </button>
                        <button
                          onClick={handleAddSampleToCart}
                          disabled={addingSample}
                          className="px-4 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors disabled:opacity-50"
                        >
                          {addingSample ? 'Adding...' : 'Add Sample £4.99'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Selected HEXIS Color Display - Below Browse Button */}
                {selectedHexisColor && !showHexisPalette && !sampleMode && (
                  <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border-2 border-green-200">
                    <div className="w-12 h-12 rounded-lg border border-gray-300 flex-shrink-0 relative overflow-hidden shadow-md">
                      <div 
                        className="absolute inset-0"
                        style={{ backgroundColor: selectedHexisColor.hex }}
                      />
                      <div 
                        className="absolute inset-0"
                        style={{
                          background: `linear-gradient(145deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.15) 25%, rgba(255,255,255,0) 50%, rgba(0,0,0,0.05) 75%, rgba(0,0,0,0.15) 100%)`,
                        }}
                      />
                      <div 
                        className="absolute top-0 left-0 right-0 h-1/3"
                        style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 100%)' }}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-green-700 font-medium uppercase tracking-wider">Selected Colour</p>
                      <p className="font-bold text-base truncate">{selectedHexisColor.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {selectedHexisColor.code}
                        {selectedHexisColor.series && ` • ${selectedHexisColor.series}`}
                        {selectedHexisColor.finish && ` • ${selectedHexisColor.finish}`}
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedHexisColor(null)}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                    >
                      Change
                    </button>
                  </div>
                )}
                
                {/* HEXIS Color Palette Panel */}
                {showHexisPalette && (
                  <div className={`mt-3 p-4 border-2 rounded-lg bg-white ${sampleMode ? 'border-amber-500' : 'border-black'}`}>
                    {sampleMode && (
                      <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-sm font-medium text-amber-800">
                          Select a colour for your A5 Sample Swatch (148 x 210mm) - £4.99
                        </p>
                      </div>
                    )}
                    <HexisColorPalette
                      onSelectColor={(color) => {
                        if (sampleMode) {
                          setSampleColor(color);
                          setShowHexisPalette(false);
                          toast.success(`Sample colour selected: ${color.name}`, {
                            description: `Click "Add Sample £4.99" to add to cart`
                          });
                          // Scroll back to product info after selection
                          setTimeout(() => {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }, 100);
                        } else {
                          // Set as pending color - don't close menu yet
                          setPendingHexisColor(color);
                          toast.info(`${color.name} selected`, {
                            description: `Click "Confirm Colour" to apply`
                          });
                        }
                      }}
                      selectedColor={sampleMode ? sampleColor : (pendingHexisColor || selectedHexisColor)}
                      showAllSeries={true}
                    />
                    
                    {/* Confirm Colour Button - Only show when a pending color is selected */}
                    {!sampleMode && pendingHexisColor && (
                      <div className="sticky bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-lg">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-lg border-2 border-gray-300 shadow-md"
                              style={{ backgroundColor: pendingHexisColor.hex }}
                            />
                            <div>
                              <p className="font-bold text-sm">{pendingHexisColor.name}</p>
                              <p className="text-xs text-gray-500">{pendingHexisColor.code}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setPendingHexisColor(null)}
                              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => {
                                setSelectedHexisColor(pendingHexisColor);
                                setSelectedColor(null);
                                setPendingHexisColor(null);
                                setShowHexisPalette(false);
                                toast.success(`Confirmed: ${pendingHexisColor.name}`, {
                                  description: `${pendingHexisColor.code} - ${pendingHexisColor.series || 'HEXIS'} ${pendingHexisColor.finish || ''}`
                                });
                                // Scroll back to product info after confirmation
                                setTimeout(() => {
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }, 100);
                              }}
                              className="px-6 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                            >
                              Confirm Colour
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Quantity Selector - Pill Shape */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-900 uppercase tracking-wide block">
                {isWrapVinylProduct ? 'Quantity (Length)' : 'Quantity'}
              </label>
              {isWrapVinylProduct && (
                <div className="text-sm text-gray-600 space-y-1">
                  <p>Qty: 1 = 1000mm x 1520mm</p>
                  <p>Qty: 2 = 2000mm x 1520mm</p>
                  <p className="text-green-600 font-semibold mt-2">💰 Qty: 25+ = £26.75 each (bulk discount)</p>
                </div>
              )}
              <div className="inline-flex items-center border-2 border-gray-300 rounded-full overflow-hidden bg-white">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition-colors"
                  data-testid="decrease-qty"
                >
                  <Minus className="h-3.5 w-3.5 text-gray-600" />
                </button>
                <input
                  type="number"
                  min="1"
                  max={product.stock || 999}
                  value={quantity}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    setQuantity(Math.max(1, Math.min(product.stock || 999, val)));
                  }}
                  className="w-16 text-center font-semibold text-sm text-gray-900 border-0 focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  data-testid="quantity-input"
                />
                <button
                  onClick={() => setQuantity(Math.min(product.stock || 999, quantity + 1))}
                  className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition-colors"
                  data-testid="increase-qty"
                >
                  <Plus className="h-3.5 w-3.5 text-gray-600" />
                </button>
              </div>
              
              {/* Show discounted price for wrap vinyl when qty >= 25 */}
              {isWrapVinylProduct && quantity >= 25 && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700 font-semibold">
                    ✓ Bulk discount applied: £26.75 each
                  </p>
                  <p className="text-xs text-green-600">
                    Total: £{(26.75 * quantity).toFixed(2)}
                  </p>
                </div>
              )}
            </div>

            {/* Application Tools Section - Only for Mini Products */}
            {vinylTools.length > 0 && (
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                    Application Tools
                  </label>
                  <p className="text-sm text-gray-600 mt-1">Don't forget your Application Tools</p>
                </div>
                
                <div className="relative" ref={toolsDropdownRef}>
                  <button
                    onClick={() => setShowToolsDropdown(!showToolsDropdown)}
                    className="w-full flex items-center justify-between px-5 py-3 bg-white border-2 border-gray-300 rounded-full hover:border-gray-400 transition-all text-left"
                    data-testid="vinyl-tools-dropdown"
                  >
                    <span className="text-sm text-gray-700">
                      {selectedTools.length > 0 
                        ? `${selectedTools.length} tool${selectedTools.length > 1 ? 's' : ''} selected`
                        : 'Select application tools...'}
                    </span>
                    <svg className={`w-5 h-5 text-gray-500 transition-transform ${showToolsDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {showToolsDropdown && (
                    <div className="absolute z-20 w-full mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                      {vinylTools.map((tool) => {
                        const isSelected = selectedTools.some(t => t.id === tool.id);
                        return (
                          <button
                            key={tool.id}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedTools(selectedTools.filter(t => t.id !== tool.id));
                              } else {
                                setSelectedTools([...selectedTools, tool]);
                              }
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${isSelected ? 'bg-green-50' : ''}`}
                          >
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-black border-black' : 'border-gray-300'}`}>
                              {isSelected && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            {/* Product Image */}
                            <div className="w-10 h-10 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                              <img 
                                src={tool.image_url || tool.images?.[0] || "https://images.unsplash.com/photo-1581783898377-1c85bf937427?w=100"} 
                                alt={tool.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "https://images.unsplash.com/photo-1581783898377-1c85bf937427?w=100";
                                  }}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{tool.name}</p>
                              </div>
                              <span className="text-sm font-bold text-gray-900">
                                {formatPrice(tool.price)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                {/* Selected Tools Summary */}
                {selectedTools.length > 0 && (
                  <div className="mt-4 space-y-2 pt-4 border-t border-gray-200">
                    {selectedTools.map((tool) => (
                      <div key={tool.id} className="flex items-center justify-between bg-white px-3 py-2 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className="w-8 h-8 rounded bg-gray-100 overflow-hidden flex-shrink-0">
                            <img 
                              src={tool.image_url || tool.images?.[0] || "https://images.unsplash.com/photo-1581783898377-1c85bf937427?w=100"} 
                              alt={tool.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <span className="text-sm text-gray-700 truncate">{tool.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-900">{formatPrice(tool.price)}</span>
                          <button
                            onClick={() => setSelectedTools(selectedTools.filter(t => t.id !== tool.id))}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-sm font-medium text-gray-600">Tools Total:</span>
                      <span className="text-sm font-bold text-gray-900">
                        {formatPrice(selectedTools.reduce((sum, tool) => sum + tool.price, 0))}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Order Instructions - Above Add to Cart */}
            <div className="space-y-3">
              <label className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                Special Instructions
              </label>
              <textarea
                value={orderInstructions}
                onChange={(e) => setOrderInstructions(e.target.value)}
                placeholder="Add any special requirements (e.g., custom text, specific details)..."
                className="w-full min-h-[100px] p-4 border-2 border-gray-200 rounded-lg bg-gray-50 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-black focus:border-black focus:bg-white transition-all placeholder:text-gray-400"
                data-testid="order-instructions"
              />
            </div>

            {/* Add to Cart and Wishlist */}
            <div className="flex gap-3 pt-4">
              <Button
                size="lg"
                className="flex-1 h-14 rounded-full bg-black text-white font-bold text-base uppercase tracking-wide hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl"
                onClick={handleAddToCart}
                disabled={adding}
                data-testid="add-to-cart-btn"
              >
                {adding ? (
                  t('loading') || "Adding..."
                ) : (
                  <>
                    <ShoppingCart className="mr-3 h-5 w-5" />
                    {t('addToCart')}
                  </>
                )}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className={`h-14 w-14 rounded-full border-2 transition-all ${
                  isInWishlist(product.id) 
                    ? 'bg-red-50 border-red-500 text-red-500 hover:bg-red-100' 
                    : 'border-gray-200 hover:border-red-500 hover:text-red-500 hover:bg-red-50'
                }`}
                onClick={handleWishlistToggle}
                data-testid="wishlist-toggle-btn"
              >
                <Heart 
                  className={`h-6 w-6 ${isInWishlist(product.id) ? 'fill-red-500' : ''}`} 
                />
              </Button>
            </div>
            
            {/* Action Tabs - Wishlist, Share */}
            <div className="flex items-center gap-8 pt-6 border-t border-gray-100 mt-6">
              {/* Wishlist Tab */}
              <button
                onClick={handleWishlistToggle}
                className={`flex items-center gap-2 text-sm transition-colors ${
                  isInWishlist(product.id) 
                    ? 'text-red-500 hover:text-red-600' 
                    : 'text-gray-500 hover:text-red-500'
                }`}
                data-testid="wishlist-tab"
              >
                <Heart className={`h-5 w-5 ${isInWishlist(product.id) ? 'fill-red-500' : ''}`} />
                <span>{t('wishlist')}</span>
              </button>
              
              {/* Share Tab with Dropdown (includes Email) */}
              <div className="relative">
                <button
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  data-testid="share-tab"
                >
                  <Share2 className="h-5 w-5" />
                  <span>{t('share')}</span>
                </button>
                
                {/* Share Dropdown Menu */}
                {showShareMenu && (
                  <div 
                    className="absolute left-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[160px] py-1"
                    onMouseLeave={() => setShowShareMenu(false)}
                  >
                    <button
                      onClick={handleEmailFriend}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                      data-testid="email-share-btn"
                    >
                      <Mail className="h-4 w-4 text-gray-500" />
                      Email a Friend
                    </button>
                    <div className="border-t border-gray-100 my-1"></div>
                    <button
                      onClick={() => handleShare('facebook')}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <svg className="h-4 w-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Facebook
                    </button>
                    <button
                      onClick={() => handleShare('twitter')}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                      X (Twitter)
                    </button>
                    <button
                      onClick={() => handleShare('whatsapp')}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      WhatsApp
                    </button>
                    <button
                      onClick={() => handleShare('pinterest')}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <svg className="h-4 w-4 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0a12 12 0 00-4.373 23.178c-.07-.937-.133-2.377.028-3.4.146-.925.943-5.893.943-5.893s-.24-.482-.24-1.195c0-1.12.65-1.955 1.455-1.955.687 0 1.018.515 1.018 1.132 0 .69-.44 1.722-.667 2.678-.19.802.402 1.456 1.192 1.456 1.43 0 2.53-1.507 2.53-3.683 0-1.927-1.384-3.272-3.363-3.272-2.29 0-3.637 1.72-3.637 3.497 0 .693.267 1.435.6 1.838a.24.24 0 01.056.231c-.061.255-.198.803-.224.915-.035.148-.116.18-.268.108-.999-.465-1.624-1.926-1.624-3.1 0-2.523 1.834-4.84 5.286-4.84 2.775 0 4.932 1.977 4.932 4.62 0 2.757-1.739 4.976-4.151 4.976-.81 0-1.573-.421-1.834-.919l-.5 1.901c-.18.695-.668 1.566-.994 2.097A12 12 0 1012 0z"/>
                      </svg>
                      Pinterest
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-16 border-t border-border pt-12">
          <h2 className="text-2xl font-bold mb-8">Product Reviews</h2>
          
          {/* Existing Reviews */}
          {reviews.length > 0 ? (
            <div className="space-y-6 mb-12">
              {reviews.map((review, index) => (
                <div key={index} className="border-b border-border pb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`h-4 w-4 ${star <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {new Date(review.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <h4 className="font-semibold mb-1">{review.title}</h4>
                  <p className="text-muted-foreground">{review.text}</p>
                  {review.user_name && (
                    <p className="text-sm text-muted-foreground mt-2">- {review.user_name}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground mb-8">No reviews yet. Be the first to review this product!</p>
          )}

          {/* Write Review Form */}
          <div className="bg-secondary p-6 rounded-lg">
            <h3 className="text-xl font-semibold mb-4">Write Your Own Review</h3>
            {!isAuthenticated && (
              <p className="text-sm text-muted-foreground mb-4">
                <Link to="/login" className="text-primary hover:underline">Sign in</Link> to leave a review, or continue as a guest.
              </p>
            )}
            
            <form onSubmit={handleSubmitReview} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Review Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={reviewTitle}
                  onChange={(e) => setReviewTitle(e.target.value)}
                  placeholder="Summarize your review"
                  className="w-full p-3 border border-border bg-background rounded focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="review-title-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">
                  Review Text <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  placeholder="Tell others about your experience with this product"
                  rows={4}
                  className="w-full p-3 border border-border bg-background rounded resize-y focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="review-text-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">
                  Rating <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">Bad</span>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="p-1 transition-transform hover:scale-110"
                        data-testid={`rating-star-${star}`}
                      >
                        <Star
                          className={`h-8 w-8 transition-colors ${
                            star <= (hoverRating || rating)
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300 hover:text-yellow-200'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                  <span className="text-sm text-muted-foreground">Excellent</span>
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={submittingReview}
                className="rounded-full uppercase tracking-wider"
                data-testid="submit-review-btn"
              >
                {submittingReview ? "Submitting..." : "Submit Review"}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* File Manager Image Picker Modal for TinyMCE */}
      {showImagePicker && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="tinymce-image-picker">
          <div className="fixed inset-0 bg-black/70" onClick={() => setShowImagePicker(false)} />
          <div className="relative z-[10000] w-full max-w-4xl max-h-[85vh] bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col mx-4">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-purple-50">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <ImageIcon className="h-5 w-5 text-purple-600" />
                Insert Image
              </h2>
              <button onClick={() => setShowImagePicker(false)} className="p-1 hover:bg-gray-200 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Tabs: File Manager | Upload | External URL */}
            <div className="flex border-b">
              <button
                onClick={() => setImagePickerTab("filemanager")}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  imagePickerTab === "filemanager" 
                    ? "text-purple-600 border-b-2 border-purple-600 bg-purple-50" 
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                <ImageIcon className="h-4 w-4 inline mr-2" />
                File Manager
              </button>
              <button
                onClick={() => setImagePickerTab("upload")}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  imagePickerTab === "upload" 
                    ? "text-purple-600 border-b-2 border-purple-600 bg-purple-50" 
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Upload className="h-4 w-4 inline mr-2" />
                Upload New
              </button>
              <button
                onClick={() => setImagePickerTab("external")}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  imagePickerTab === "external" 
                    ? "text-purple-600 border-b-2 border-purple-600 bg-purple-50" 
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Link className="h-4 w-4 inline mr-2" />
                External URL
              </button>
            </div>

            {/* File Manager Tab */}
            {imagePickerTab === "filemanager" && (
              <>
                {/* Search */}
                <div className="p-4 border-b bg-gray-50">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search images..."
                      value={imageSearchTerm}
                      onChange={(e) => setImageSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>

                {/* Image Grid */}
                <div className="flex-1 overflow-y-auto p-4">
                  {loadingImages ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                      {fileManagerImages
                        .filter(img => !imageSearchTerm || 
                          img.source_name?.toLowerCase().includes(imageSearchTerm.toLowerCase()) ||
                          img.url?.toLowerCase().includes(imageSearchTerm.toLowerCase())
                        )
                        .map((img) => (
                          <button
                            key={img.id}
                            onClick={() => {
                              if (imagePickerCallback) {
                                const fullUrl = img.url.startsWith('http') ? img.url : `${window.location.origin}${img.url}`;
                                imagePickerCallback(fullUrl, { alt: img.source_name || '' });
                              }
                              setShowImagePicker(false);
                              setImageSearchTerm("");
                            }}
                            className="group relative aspect-square bg-white border rounded-lg overflow-hidden hover:ring-2 hover:ring-purple-500 transition-all"
                          >
                            <img
                              src={img.url}
                              alt={img.source_name}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.src = "https://via.placeholder.com/150?text=Error"; }}
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="bg-white rounded-full p-2">
                                  <Check className="h-5 w-5 text-purple-600" />
                                </div>
                              </div>
                            </div>
                            <div className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-medium text-white ${
                              img.source === "category" ? "bg-green-500" :
                              img.source === "upload" ? "bg-purple-500" :
                              img.source === "advert" ? "bg-orange-500" : "bg-gray-500"
                            }`}>
                              {img.source}
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] p-1 truncate">
                              {img.source_name}
                            </div>
                          </button>
                        ))}
                    </div>
                  )}
                  {!loadingImages && fileManagerImages.length === 0 && (
                    <div className="text-center py-12">
                      <ImageIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No images found in File Manager</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex justify-between items-center p-4 border-t bg-gray-50">
                  <p className="text-xs text-gray-500">
                    {fileManagerImages.filter(img => !imageSearchTerm || 
                      img.source_name?.toLowerCase().includes(imageSearchTerm.toLowerCase())
                    ).length} images available
                  </p>
                  <Button variant="outline" onClick={() => setShowImagePicker(false)}>
                    Cancel
                  </Button>
                </div>
              </>
            )}

            {/* Upload Tab */}
            {imagePickerTab === "upload" && (
              <div className="flex-1 p-6">
                <div
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                    uploadingPickerImage 
                      ? "border-purple-400 bg-purple-50" 
                      : "border-gray-300 hover:border-purple-400 hover:bg-purple-50"
                  }`}
                >
                  {uploadingPickerImage ? (
                    <div className="py-8">
                      <Loader2 className="h-12 w-12 animate-spin text-purple-500 mx-auto mb-4" />
                      <p className="text-purple-600 font-medium">Uploading image...</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 font-medium mb-2">Drag and drop an image here</p>
                      <p className="text-gray-400 text-sm mb-4">or</p>
                      <label className="inline-block">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            
                            setUploadingPickerImage(true);
                            const formData = new FormData();
                            formData.append("file", file);
                            
                            try {
                              const token = localStorage.getItem("auth_token");
                              const res = await axios.post(`${API}/admin/website-images/upload`, formData, {
                                headers: { 
                                  Authorization: `Bearer ${token}`,
                                  "Content-Type": "multipart/form-data"
                                }
                              });
                              
                              if (res.data.url && imagePickerCallback) {
                                const fullUrl = res.data.url.startsWith('http') 
                                  ? res.data.url 
                                  : `${window.location.origin}${res.data.url}`;
                                imagePickerCallback(fullUrl, { alt: file.name.replace(/\.[^/.]+$/, '') });
                                toast.success("Image uploaded and inserted");
                                setShowImagePicker(false);
                              }
                            } catch (error) {
                              toast.error("Failed to upload image");
                              console.error(error);
                            } finally {
                              setUploadingPickerImage(false);
                              e.target.value = "";
                            }
                          }}
                        />
                        <span className="px-6 py-2 bg-purple-600 text-white rounded-lg cursor-pointer hover:bg-purple-700 transition-colors">
                          Choose File
                        </span>
                      </label>
                      <p className="text-xs text-gray-400 mt-4">Supports: JPG, PNG, WebP, GIF (Max 5MB)</p>
                    </>
                  )}
                </div>
                
                <div className="flex justify-end mt-6">
                  <Button variant="outline" onClick={() => setShowImagePicker(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* External URL Tab */}
            {imagePickerTab === "external" && (
              <div className="flex-1 p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
                    <input
                      type="url"
                      placeholder="https://example.com/image.jpg"
                      value={externalImageUrl}
                      onChange={(e) => setExternalImageUrl(e.target.value)}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Alt Text (optional)</label>
                    <input
                      type="text"
                      placeholder="Image description for accessibility"
                      value={externalImageAlt}
                      onChange={(e) => setExternalImageAlt(e.target.value)}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  
                  {externalImageUrl && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Preview</p>
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <img
                          src={externalImageUrl}
                          alt={externalImageAlt || "Preview"}
                          className="max-h-48 mx-auto rounded"
                          onError={(e) => { e.target.src = "https://via.placeholder.com/200x150?text=Invalid+URL"; }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex justify-end gap-3 mt-6">
                  <Button variant="outline" onClick={() => setShowImagePicker(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => {
                      if (externalImageUrl && imagePickerCallback) {
                        imagePickerCallback(externalImageUrl, { alt: externalImageAlt || '' });
                        setShowImagePicker(false);
                        setExternalImageUrl("");
                        setExternalImageAlt("");
                      }
                    }}
                    disabled={!externalImageUrl}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Insert Image
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Related Products Picker Modal */}
      {showRelatedPicker && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="related-products-picker">
          <div className="fixed inset-0 bg-black/70" onClick={() => setShowRelatedPicker(false)} />
          <div className="relative z-[10000] w-full max-w-4xl max-h-[85vh] bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col mx-4">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-blue-50">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-600" />
                Add Related Products
              </h2>
              <button onClick={() => setShowRelatedPicker(false)} className="p-1 hover:bg-gray-200 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Search */}
            <div className="p-4 border-b bg-gray-50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products by name..."
                  value={relatedSearchTerm}
                  onChange={(e) => setRelatedSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Products Grid */}
            <div className="flex-1 overflow-y-auto p-4">
              {loadingAllProducts ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                </div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {allProducts
                    .filter(p => !relatedSearchTerm || 
                      p.name?.toLowerCase().includes(relatedSearchTerm.toLowerCase())
                    )
                    .map((prod) => {
                      const isSelected = editForm.related_products?.includes(prod.id);
                      return (
                        <button
                          key={prod.id}
                          type="button"
                          onClick={() => {
                            if (isSelected) {
                              setEditForm({
                                ...editForm,
                                related_products: editForm.related_products.filter(id => id !== prod.id)
                              });
                            } else {
                              setEditForm({
                                ...editForm,
                                related_products: [...(editForm.related_products || []), prod.id]
                              });
                            }
                          }}
                          className={`relative text-left bg-white rounded-lg border p-2 transition-all ${
                            isSelected 
                              ? "ring-2 ring-blue-500 border-blue-500" 
                              : "hover:border-blue-300 hover:shadow-md"
                          }`}
                        >
                          {isSelected && (
                            <div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full p-0.5">
                              <Check className="h-3 w-3" />
                            </div>
                          )}
                          <div className="aspect-square bg-gray-100 rounded overflow-hidden mb-2">
                            {prod.image_url ? (
                              <img
                                src={prod.image_url}
                                alt={prod.name}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.src = "https://via.placeholder.com/100?text=No+Image"; }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <ImageIcon className="h-6 w-6" />
                              </div>
                            )}
                          </div>
                          <p className="text-xs font-medium text-gray-700 truncate" title={prod.name}>
                            {prod.name}
                          </p>
                          <p className="text-xs text-gray-500">£{prod.price?.toFixed(2)}</p>
                        </button>
                      );
                    })}
                </div>
              )}
              {!loadingAllProducts && allProducts.length === 0 && (
                <div className="text-center py-12">
                  <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No products available</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center p-4 border-t bg-gray-50">
              <p className="text-sm text-gray-600">
                <span className="font-medium text-blue-600">{editForm.related_products?.length || 0}</span> products selected
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowRelatedPicker(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => setShowRelatedPicker(false)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Done
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
